import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InitiativeStatus } from '../../../../api/initiative-client/initiative-client.type';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { setSelectedInitiativeStatuses } from '../../../../store/process-analysis-store/process-analysis-store.actions';
import {
  useComparisonMode,
  useFilterWasTouched,
  useSelectedInitiativeStatuses,
} from '../../../../store/process-analysis-store/process-analysis-store.hooks';
import { useInitiativesData } from '../../hooks/initiative-data';
import { ComparisonMode } from '../../process-analysis.type';
import { getStatusColor, getStatusLabel } from './initiative-overview.helpers';
import type { StatusOption } from './initiative-overview.type';

const STATUS_ORDER: InitiativeStatus[] = [
  InitiativeStatus.OnTrack,
  InitiativeStatus.AtRisk,
  InitiativeStatus.OffTrack,
  InitiativeStatus.Unknown,
  InitiativeStatus.Empty,
];

/**
 * Custom hook that extracts unique statuses from fetched initiatives
 * and creates status options with labels and colors, sorted by enum order
 *
 * @returns {StatusOption[]} Array of available status options sorted by enum order
 */
const useAvailableInitiativeStatuses = (): StatusOption[] => {
  const { t } = useTranslation();
  const initiatives = useProcessAnalysisStore((state) => state.initiatives);

  return useMemo(() => {
    const statusSet = new Set<InitiativeStatus>();

    initiatives.forEach((initiative) => {
      const status = initiative.status?.by_tasks?.status || initiative.status?.by_points?.status;
      if (status) {
        statusSet.add(status);
      }
    });

    return STATUS_ORDER.filter((status) => statusSet.has(status)).map(
      (status): StatusOption => ({
        status,
        label: getStatusLabel(status, t),
        color: getStatusColor(status),
      }),
    );
  }, [initiatives, t]);
};

/**
 * Hook to handle auto-selection of initiative statuses when filters haven't been touched by the user
 *
 * @returns {void}
 */
const useAutoSelectFilters = (): void => {
  const selectedInitiativeStatuses = useSelectedInitiativeStatuses();
  const { isFetching } = useInitiativesData();
  const availableStatuses = useAvailableInitiativeStatuses();
  const comparisonMode = useComparisonMode();
  const initiativeStatusesWasTouched = useFilterWasTouched('selectedInitiativeStatuses');

  useEffect(() => {
    if (isFetching || availableStatuses.length === 0 || initiativeStatusesWasTouched) return;

    const isSingleMode = comparisonMode === ComparisonMode.Single;
    const noCurrentSelection = selectedInitiativeStatuses.length === 0;
    const shouldAutoSelectAllStatuses = isSingleMode && noCurrentSelection;

    if (shouldAutoSelectAllStatuses) {
      const allStatuses = availableStatuses.map((statusOption) => statusOption.status);
      setSelectedInitiativeStatuses(allStatuses);
    }
  }, [isFetching, availableStatuses, selectedInitiativeStatuses.length, comparisonMode, initiativeStatusesWasTouched]);
};

export { useAutoSelectFilters, useAvailableInitiativeStatuses };
